import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Hourglass, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABEL = {
  ended: { label: "Finalizada", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  ended_by_admin: { label: "Cerrada por admin", cls: "bg-blue-500/15 text-blue-300 border-blue-500/30" },
  expired: { label: "Expirada", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  cancelled: { label: "Cancelada", cls: "bg-red-500/15 text-red-300 border-red-500/30" },
};

/**
 * Archive of all closed/expired/cancelled Vidente Virtual sessions.
 * Calls GET /api/admin/vidente/sessions/closed.
 * Has filter chips (Todas / Finalizadas / Cerradas por admin / Expiradas / Canceladas).
 */
export function VidenteClosedSessionsSection() {
  const { authHeader } = useAuth();
  const [data, setData] = useState({ items: [], total_by_status: {} });
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState(""); // "" = all

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API}/admin/vidente/sessions/closed?limit=200${filter ? `&status=${filter}` : ""}`;
      const r = await axios.get(url, { headers: authHeader() });
      setData(r.data || { items: [], total_by_status: {} });
    } catch {
      setData({ items: [], total_by_status: {} });
    } finally {
      setLoading(false);
    }
  }, [authHeader, filter]);

  useEffect(() => { load(); }, [load]);

  const totals = data.total_by_status || {};

  return (
    <div className="mt-8 glass-dark rounded-2xl overflow-hidden" data-testid="vidente-closed-sessions">
      <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-white/5">
        <div>
          <h3 className="font-serif text-lg text-white flex items-center gap-2">
            <Hourglass className="w-4 h-4 text-[#d4af37]" /> Sesiones cerradas
          </h3>
          <p className="text-[11px] text-neutral-500 mt-0.5">Historial de sesiones expiradas, finalizadas o canceladas.</p>
        </div>
        <button
          type="button"
          onClick={load}
          data-testid="closed-refresh"
          className="text-[10px] uppercase tracking-widest text-neutral-300 hover:text-white border border-white/10 hover:border-[#d4af37]/40 rounded-full px-3 py-1.5"
        >
          Refrescar
        </button>
      </div>

      <div className="flex flex-wrap gap-2 px-4 py-3 border-b border-white/5 bg-black/30">
        {[
          { id: "", label: "Todas", count: Object.values(totals).reduce((a, b) => a + b, 0) },
          { id: "ended", label: "Finalizadas", count: totals.ended || 0 },
          { id: "ended_by_admin", label: "Cerradas por admin", count: totals.ended_by_admin || 0 },
          { id: "expired", label: "Expiradas", count: totals.expired || 0 },
          { id: "cancelled", label: "Canceladas", count: totals.cancelled || 0 },
        ].map((f) => (
          <button
            key={f.id || "all"}
            type="button"
            onClick={() => setFilter(f.id)}
            data-testid={`closed-filter-${f.id || "all"}`}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] uppercase tracking-widest transition-all ${
              filter === f.id
                ? "bg-[#d4af37] text-black font-bold"
                : "bg-black/40 border border-white/10 text-neutral-300 hover:text-white hover:border-[#d4af37]/40"
            }`}
          >
            {f.label}
            <span className={`text-[10px] rounded-full px-1.5 py-0.5 ${filter === f.id ? "bg-black/20 text-black" : "bg-white/5 text-neutral-400"}`}>
              {f.count}
            </span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="p-10 text-center"><Loader2 className="w-5 h-5 animate-spin text-[#d4af37] mx-auto" /></div>
      ) : data.items.length === 0 ? (
        <div className="p-10 text-center text-sm text-neutral-500 italic">Sin sesiones cerradas en este filtro.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-black/40 text-[10px] uppercase tracking-widest text-neutral-400">
                <th className="p-3 text-left">Usuario</th>
                <th className="p-3 text-center">Estado</th>
                <th className="p-3 text-center">Duración</th>
                <th className="p-3 text-center">Turnos</th>
                <th className="p-3 text-center">Fases tirada</th>
                <th className="p-3 text-left">Iniciada</th>
                <th className="p-3 text-left">Terminada</th>
                <th className="p-3 text-left">Primer mensaje del usuario</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {data.items.map((s) => {
                const st = STATUS_LABEL[s.status] || { label: s.status, cls: "bg-neutral-700/40 text-neutral-300" };
                return (
                  <tr key={s.id} className="hover:bg-white/5" data-testid={`closed-row-${s.id}`}>
                    <td className="p-3">
                      <div className="text-white">{s.user_name || "—"}</div>
                      <div className="text-[10px] text-neutral-500 font-mono">{s.id.slice(0, 8)}…</div>
                    </td>
                    <td className="p-3 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest border ${st.cls}`}>
                        {st.label}
                      </span>
                    </td>
                    <td className="p-3 text-center text-neutral-200">{s.duration_minutes != null ? `${s.duration_minutes}m` : "—"}</td>
                    <td className="p-3 text-center text-neutral-300">{s.user_turns}/{s.message_count}</td>
                    <td className="p-3 text-center text-neutral-300">{(s.phases_drawn || []).length}</td>
                    <td className="p-3 text-neutral-400">{s.started_at ? new Date(s.started_at).toLocaleString() : "—"}</td>
                    <td className="p-3 text-neutral-400">{s.ended_at ? new Date(s.ended_at).toLocaleString() : "—"}</td>
                    <td className="p-3 text-neutral-200 max-w-[280px] truncate">
                      {s.first_user_message || <span className="text-neutral-600 italic">sin mensajes</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
